import React from 'react';

import {
  EMPTY_VALUE,
  formatDate,
  formatDateTime,
  formatRelativeTime,
} from '../lib/formatters';

type TimestampValue = string | number | Date | null | undefined;

interface TimestampProps {
  value: TimestampValue;
  /** Fixed-zone calendar days (license key expiry): applied to the text and the hover title. */
  timeZone?: string;
  className?: string;
}

export interface DateCellProps extends TimestampProps {
  /**
   * 'date' (default) shows "Sep 3, 2026" with the date-time on hover. 'datetime'
   * keeps the time visible — only for audit rows (License Keys Created) and
   * dialog metadata.
   */
  precision?: 'date' | 'datetime';
}

/**
 * Parse anything the API sends — ISO strings, epoch numbers, and Postgres
 * `::text` timestamps ('2026-01-01 00:00:00+00') — into a valid Date, or null.
 */
function toDate(value: TimestampValue): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  let date = new Date(value);
  if (Number.isNaN(date.getTime()) && typeof value === 'string') {
    // pg text form: space instead of 'T', and a bare '+00' offset.
    const normalized = value.trim().replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00');
    date = new Date(normalized);
  }
  return Number.isNaN(date.getTime()) ? null : date;
}

const Absent: React.FC<{ className?: string }> = ({ className }) => (
  <span className={className}>{EMPTY_VALUE}</span>
);

/**
 * A record-history date in a table (Created, Updated, Expires, Joined, Invited):
 * `<time dateTime>` with the date only, and the full date-time as the hover
 * title — the time is never lost, it just isn't noise in the column.
 */
export const DateCell: React.FC<DateCellProps> = ({ value, timeZone, className, precision = 'date' }) => {
  const date = toDate(value);
  if (!date) return <Absent className={className} />;
  const options = timeZone ? { timeZone } : undefined;
  const text = precision === 'datetime' ? formatDateTime(date, options) : formatDate(date, options);
  return (
    <time dateTime={date.toISOString()} title={formatDateTime(date, options)} className={className}>
      {text}
    </time>
  );
};

/**
 * Recency in a table (Last seen, Last sign-in): "5 minutes ago" with the exact
 * date-time on hover. Rendered once — it is not a ticker and never a live region.
 */
export const RelativeTime: React.FC<TimestampProps> = ({ value, timeZone, className }) => {
  const date = toDate(value);
  if (!date) return <Absent className={className} />;
  return (
    <time
      dateTime={date.toISOString()}
      title={formatDateTime(date, timeZone ? { timeZone } : undefined)}
      className={className}
    >
      {formatRelativeTime(date)}
    </time>
  );
};
